// ============================================
// INSTALACION.JS — todo lo que sabe la app sobre instalarse a si misma
// ============================================
// POR QUE EXISTE (INSTALAR-APP, 2026-09-14)
// Beep-OS ya era instalable, pero la opcion vivia escondida en el menu del navegador. Ricardo no
// sabia que existia y su equipo de campo tampoco. Aqui se concentra lo que hace falta para sacarla
// a la superficie, en UN solo sitio: el menu de Mi cuenta y la barra de bienvenida leen de aqui.
//
// LO IMPORTANTE DE `beforeinstallprompt`: el navegador lo dispara UNA vez, temprano, y si nadie lo
// guarda se pierde para siempre — no se puede pedir de nuevo. Por eso se captura en cuanto carga el
// modulo (abajo del todo), no cuando se monta un componente: si se esperara al montaje, el evento
// ya habria pasado y el boton "Instalar" no funcionaria nunca.

import { storage, STORAGE_KEYS } from './storage';

// ---- Estado del prompt nativo, capturado a nivel de modulo ----
let promptGuardado = null;
const suscriptores = new Set();

function avisar() {
  for (const f of suscriptores) { try { f(); } catch { /* un suscriptor roto no tumba a los demas */ } }
}

export function suscribirInstalacion(f) {
  suscriptores.add(f);
  return () => suscriptores.delete(f);
}

export function hayPromptNativo() { return !!promptGuardado; }

// Lanza la instalacion nativa. Devuelve 'aceptada' | 'rechazada' | 'sin-prompt'.
export async function instalarNativo() {
  if (!promptGuardado) return 'sin-prompt';
  const p = promptGuardado;
  promptGuardado = null;          // el navegador NO deja reusar el mismo evento
  avisar();
  try {
    p.prompt();
    const r = await p.userChoice;
    return r && r.outcome === 'accepted' ? 'aceptada' : 'rechazada';
  } catch {
    return 'rechazada';
  }
}

// ---- ¿La app YA corre instalada? ----
// Dos señales, porque ningun navegador las da todas: el modo de presentacion (Android, escritorio)
// y la bandera propia de Safari en iOS.
export function corriendoInstalada() {
  if (typeof window === 'undefined') return false;
  const porDisplay = ['standalone', 'fullscreen', 'minimal-ui']
    .some(m => window.matchMedia && window.matchMedia(`(display-mode: ${m})`).matches);
  const porSafari = window.navigator && window.navigator.standalone === true;
  return porDisplay || porSafari;
}

// ---- ¿Que plataforma es, para decidir que enseñar? ----
export function plataforma() {
  if (typeof navigator === 'undefined') return 'otro';
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua)
    // iPadOS 13+ se hace pasar por Mac; se distingue porque tiene pantalla tactil.
    || (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document);
  if (iOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'escritorio';
}

// Que le toca ver a esta persona en la entrada del menu.
//   'instalada'      -> ya la tiene: se enseña en gris, sin accion
//   'boton'          -> el navegador permite instalar: boton activo
//   'instrucciones'  -> no hay prompt nativo (iOS siempre, y algun navegador suelto): se explica a mano
export function estadoInstalacion() {
  if (corriendoInstalada()) return 'instalada';
  if (hayPromptNativo()) return 'boton';
  return 'instrucciones';
}

// ---- La barra de bienvenida: se enseña UNA vez y se respeta la decision ----
// La clave lleva el usuario para que en un equipo compartido la decision de uno no calle el aviso
// al otro. Sin `storage` aqui seria una molestia diaria, que es justo lo que Ricardo pidio evitar.
function claveAviso(usuarioId) {
  return `${usuarioId || 'anon'}`;
}

function leerDecisiones() {
  try {
    const v = storage.get(STORAGE_KEYS.INSTALAR_AVISO);
    return (v && typeof v === 'object') ? v : {};
  } catch { return {}; }
}

export function avisoYaResuelto(usuarioId) {
  return !!leerDecisiones()[claveAviso(usuarioId)];
}

export function marcarAvisoResuelto(usuarioId, motivo) {
  try {
    const m = leerDecisiones();
    m[claveAviso(usuarioId)] = { motivo: motivo || 'cerrado', cuando: Date.now() };
    storage.set(STORAGE_KEYS.INSTALAR_AVISO, m);
  } catch { /* sin espacio o modo privado: ni modo, se volvera a ver una vez */ }
}

// ¿Toca enseñar la barra? Movil, no instalada, y sin decision previa de esta persona.
export function debeMostrarAviso(usuarioId) {
  if (typeof window === 'undefined') return false;
  if (corriendoInstalada()) return false;           // ya la tiene
  if (plataforma() === 'escritorio') return false;  // el encargo la pide solo en movil
  if (avisoYaResuelto(usuarioId)) return false;     // ya dijo que no, o ya instalo
  return true;
}

// ---- Captura del evento, lo antes posible ----
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();      // sin esto Chrome enseña su propia barra y nos quita el control
    promptGuardado = e;
    avisar();
  });
  window.addEventListener('appinstalled', () => {
    promptGuardado = null;
    avisar();
  });
}
